/**
 * The team's comment threads on one post (backend #128, #125).
 *
 * GET /content-calendar/plan-slots/:slotId/comments returns every thread on
 * the post, across its concept versions and its brief: anchors with their
 * marker numbers, replies, mentions, done, edits, deletes, and M7's draft on
 * each root (`m7Change`, the team's alone). The acting routes live in
 * useCommentActions; this file is the read and the cache helpers they share.
 */
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { clampWhenHidden, POLL_FEEDBACK_MS } from "@/lib/realtime";
import { livePoll, useCalendarLive } from "../api/calendarLive";

export type CommentSurface = "concept" | "brief";

export interface ApiCommentMention {
  kind: "user" | "client";
  id: string;
  name: string;
}

export interface ApiCommentAnchor {
  part: string;
  key: string | null;
  label: string;
  number?: number;
}

/** M7's draft on a thread (backend #125). */
export interface ApiCommentDraft {
  id: string;
  state: "drafting" | "suggested" | "accepted" | "dismissed" | "failed";
  label: string;
  body: string;
  note: string | null;
  error?: string | null;
}

export interface ApiComment {
  id: string;
  surface: CommentSurface;
  /** Concept comments: the concept version. */
  versionId?: string;
  /** Brief comments: the brief. */
  briefId?: string;
  version: number | null;
  authorType: "TEAM" | "CLIENT";
  authorName: string;
  authorUserId?: string | null;
  visibility?: "SHARED" | "INTERNAL";
  body: string;
  createdAt: string;
  parentId: string | null;
  anchor: ApiCommentAnchor | null;
  mentions: ApiCommentMention[];
  resolvedAt: string | null;
  resolvedByName: string | null;
  editedAt: string | null;
  deleted: boolean;
  /** M7's line under a client's comment: what they ask the team to do. Team reads only. */
  clientIntent?: string | null;
}

export interface ApiThread extends ApiComment {
  replies: ApiComment[];
  m7Change?: ApiCommentDraft | null;
}

export interface PostThreadsResponse {
  threads: ApiThread[];
  /** Whether each surface still takes comments (closed once approved). */
  open: { concept: boolean; brief: boolean };
}

export const postThreadsKey = (slotId: string | null) => ["postThreads", slotId];

/** Every thread on a post. Comments arrive from the client, so it polls gently. */
export function usePostThreads(slotId: string | null) {
  const live = useCalendarLive();
  return useQuery({
    queryKey: postThreadsKey(slotId),
    queryFn: async () =>
      (await axiosInstance.get<PostThreadsResponse>(`/content-calendar/plan-slots/${slotId}/comments`)).data,
    enabled: !!slotId,
    refetchInterval: () => livePoll(live, clampWhenHidden(POLL_FEEDBACK_MS)),
    refetchOnWindowFocus: !live,
  });
}

// ---------------------------------------------------------------- cache

/** One comment (a root or a reply) as it was, for a narrow rollback. */
export type CommentSnapshot = { comment: ApiComment; m7Change?: ApiCommentDraft | null } | null;

/** Find one comment anywhere in the threads. */
export function findComment(data: PostThreadsResponse | undefined, commentId: string): ApiComment | null {
  for (const thread of data?.threads ?? []) {
    if (thread.id === commentId) return thread;
    const reply = thread.replies.find((r) => r.id === commentId);
    if (reply) return reply;
  }
  return null;
}

/** Patch one comment, a root or a reply, leaving every other row as it is. */
export function patchComment(
  qc: QueryClient,
  slotId: string,
  commentId: string,
  patch: (comment: ApiComment) => ApiComment,
): CommentSnapshot {
  let before: CommentSnapshot = null;
  qc.setQueryData<PostThreadsResponse>(postThreadsKey(slotId), (data) => {
    if (!data) return data;
    return {
      ...data,
      threads: data.threads.map((thread) => {
        if (thread.id === commentId) {
          before = { comment: thread };
          return { ...thread, ...patch(thread) } as ApiThread;
        }
        if (!thread.replies.some((r) => r.id === commentId)) return thread;
        return {
          ...thread,
          replies: thread.replies.map((reply) => {
            if (reply.id !== commentId) return reply;
            before = { comment: reply };
            return patch(reply);
          }),
        };
      }),
    };
  });
  return before;
}

/** Put one comment back exactly as it was (its own fields only). */
export function restoreComment(qc: QueryClient, slotId: string, snapshot: CommentSnapshot) {
  if (!snapshot) return;
  const { comment } = snapshot;
  patchComment(qc, slotId, comment.id, (current) => ({
    ...current,
    body: comment.body,
    editedAt: comment.editedAt,
    deleted: comment.deleted,
    resolvedAt: comment.resolvedAt,
    resolvedByName: comment.resolvedByName,
  }));
}

/** Patch the M7 draft on the thread that carries it. */
export function patchDraft(
  qc: QueryClient,
  slotId: string,
  draftId: string,
  next: (draft: ApiCommentDraft) => ApiCommentDraft | null,
): { threadId: string; draft: ApiCommentDraft } | null {
  let before: { threadId: string; draft: ApiCommentDraft } | null = null;
  qc.setQueryData<PostThreadsResponse>(postThreadsKey(slotId), (data) => {
    if (!data) return data;
    return {
      ...data,
      threads: data.threads.map((thread) => {
        if (thread.m7Change?.id !== draftId) return thread;
        before = { threadId: thread.id, draft: thread.m7Change };
        return { ...thread, m7Change: next(thread.m7Change) };
      }),
    };
  });
  return before;
}

/** Put a thread's draft back as it was. */
export function restoreDraft(
  qc: QueryClient,
  slotId: string,
  snapshot: { threadId: string; draft: ApiCommentDraft } | null,
) {
  if (!snapshot) return;
  qc.setQueryData<PostThreadsResponse>(postThreadsKey(slotId), (data) =>
    data
      ? {
          ...data,
          threads: data.threads.map((thread) =>
            thread.id === snapshot.threadId ? { ...thread, m7Change: snapshot.draft } : thread,
          ),
        }
      : data,
  );
}
